<template>
  <aside id="sidebar" class="w-64 bg-white min-h-screen border-r border-gray-200 fixed left-0 top-13">
    <div class="py-4">
      <div class="px-6 py-2">
        <h3 class="text-xs font-semibold text-gray-500 tracking-wider uppercase">MAIN</h3>
        <nav class="mt-2 divide-y divide-gray-100">
          <a href="/dashboard">
            <span class="block px-3 py-2 text-sm font-medium rounded-md flex
              items-center cursor-pointer" :class="isActive('/dashboard') ? 'text-green-600 bg-green-50' :
                'text-gray-700'">
              <i class="fa-solid fa-chart-line mr-3"></i>
              Dashboard
            </span></a>
          <a href="/apikeys"><span class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center
            hover:bg-gray-50 cursor-pointer" :class="isActive('/apikeys') ? 'text-green-600 bg-green-50' :
              'text-gray-700'">
              <i class="fa-solid fa-key mr-3"></i>
              API Keys
            </span></a>
          <a href="/apirequests">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/apirequests') ? 'text-green-600 bg-green-50' :
                'text-gray-700'">
              <i class="fa-solid fa-list mr-3"></i>
              API Requests
            </span>
          </a>
          <a href="/user-mgt">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/user-mgt') ? 'text-green-600 bg-green-50' :
                'text-gray-700'">
              <i class="fa-solid fa-users mr-3"></i>
              User Management
            </span>
          </a>
        </nav>
      </div>

      <!-- Verification: admin sees all, public sees only subscribed services -->
      <div class="mt-6 px-6 py-2" v-if="visibleVerificationItems.length > 0">
        <h3 class="text-xs font-semibold text-gray-500 tracking-wider uppercase">VERIFICATION</h3>
        <nav class="mt-2 divide-y divide-gray-100">
          <a v-for="item in visibleVerificationItems" :key="item.path" :href="item.path">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive(item.path) ? 'text-green-600 bg-green-50' : 'text-gray-700'">
              <i :class="`fa-solid ${item.icon} mr-3`"></i>
              {{ item.label }}
            </span>
          </a>
        </nav>
      </div>

      <div class="mt-6 px-6 py-2" v-if="hasManagementPermissions">
        <h3 class="text-xs font-semibold text-gray-500 tracking-wider uppercase">MANAGEMENT</h3>
        <nav class="mt-2 divide-y divide-gray-100">
          <a href="/management/organizations">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/management/organizations') ? 'text-green-600 bg-green-50' : 'text-gray-700'">
              <i class="fa-solid fa-building mr-3"></i>
              Organizations
            </span>
          </a>
          <a href="/management/services">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/management/services') ? 'text-green-600 bg-green-50' : 'text-gray-700'">
              <i class="fa-solid fa-cubes mr-3"></i>
              Services
            </span>
          </a>
          <a href="/management/topup-requests">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/management/topup-requests') ? 'text-green-600 bg-green-50' : 'text-gray-700'">
              <i class="fa-solid fa-coins mr-3"></i>
              Top-up Requests
            </span>
          </a>
        </nav>
      </div>

      <div class="mt-6 px-6 py-2">
        <h3 class="text-xs font-semibold text-gray-500 tracking-wider uppercase">SETTINGS</h3>
        <nav class="mt-2 divide-y divide-gray-100">
          <a href="/billing/ledger" v-if="isPublicPurpose">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer"
              :class="isActive('/billing/ledger') ? 'text-green-600 bg-green-50' : 'text-gray-700'">
              <i class="fa-solid fa-credit-card mr-3"></i>
              Billing
            </span>
          </a>
          <a @click="handleLogout" @keyup.enter="handleLogout" tabindex="0" role="button">
            <span
              class="block px-3 py-2 text-sm font-medium text-gray-700 rounded-md flex items-center hover:bg-gray-50 cursor-pointer">
              <i class="fa-solid fa-user-circle mr-3"></i>
              Log Out
            </span>
          </a>
        </nav>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useServicesStore } from '@/store/services';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const servicesStore = useServicesStore();

const isPublicPurpose = computed(() => localStorage.getItem('purpose') === 'public');

const hasManagementPermissions = computed(() => {
  const purpose = localStorage.getItem('purpose');
  if (purpose !== 'local') return false;
  const permissions = localStorage.getItem('permissions');
  if (!permissions) return false;
  const permissionList = permissions.split(',').map((p) => p.trim().toLowerCase());
  const managementPermissions = [
    'manage_organizations',
    'manage_accounts',
    'create_topup_requests',
    'approve_topup_requests',
    'view_topup_requests',
  ];
  return permissionList.some((permission) => managementPermissions.includes(permission));
});

const ALL_VERIFICATION_ITEMS = [
  { serviceName: 'PhoneValidation', path: '/validations/phone', label: 'Phone Validation', icon: 'fa-phone' },
  { serviceName: 'NINValidation', path: '/validations/id', label: 'NIN Validation', icon: 'fa-id-card' },
  { serviceName: 'NINMatchValidation', path: '/validations/id-match', label: 'NIN Match Validation', icon: 'fa-id-card' },
  { serviceName: 'KYCValidation', path: '/validations/kyc', label: 'KYC Validation', icon: 'fa-building' },
];

const visibleVerificationItems = computed(() => {
  if (!isPublicPurpose.value) {
    return ALL_VERIFICATION_ITEMS;
  }
  if (!servicesStore.subscriptions?.length) return [];
  const subscribedNames = new Set(
    servicesStore.subscriptions.map((s) => s.serviceName ?? ''),
  );
  return ALL_VERIFICATION_ITEMS.filter((item) => subscribedNames.has(item.serviceName));
});

onMounted(() => {
  if (isPublicPurpose.value) {
    const user = authStore.user as any;
    const organizationId = user?.organizationId;
    if (organizationId && !servicesStore.subscriptions.length) {
      servicesStore.fetchSubscriptions(organizationId);
    }
  }
});

function isActive(path: string) {
  return route.path === path;
}

async function handleLogout() {
  authStore.logout();
  await router.push('/login');
}
</script>
